import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Plus, Trash2, Pencil, X, Image as ImageIcon, FileText, Eye } from "lucide-react";
import {
  adminListArticles,
  upsertArticle,
  deleteArticle,
  uploadArticleImage,
} from "@/lib/articles.functions";
import type { Database } from "@/integrations/supabase/types";

type Article = Database["public"]["Tables"]["articles"]["Row"];

export const Route = createFileRoute("/admin/articles")({
  component: AdminArticlesPage,
});

const empty = {
  id: null as string | null,
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  cover_image_url: "",
  category: "",
  author: "Bacha Way",
  published: true,
};

function AdminArticlesPage() {
  const listFn = useServerFn(adminListArticles);
  const upsertFn = useServerFn(upsertArticle);
  const deleteFn = useServerFn(deleteArticle);
  const uploadFn = useServerFn(uploadArticleImage);

  const [items, setItems] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<typeof empty | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<"cover" | "inline" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const contentRef = useRef<HTMLTextAreaElement>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listFn();
      setItems(r.articles as Article[]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const fileToBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(",")[1] ?? "");
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });

  const uploadImage = async (file: File, target: "cover" | "inline") => {
    if (!editing) return;
    if (file.size > 8 * 1024 * 1024) {
      alert("Image must be under 8MB");
      return;
    }
    setUploading(target);
    try {
      const base64 = await fileToBase64(file);
      const res = await uploadFn({
        data: { filename: file.name, contentType: file.type || "image/jpeg", base64 },
      });
      if (target === "cover") {
        setEditing({ ...editing, cover_image_url: res.url });
      } else {
        const md = `\n\n![${file.name.replace(/\.[^.]+$/, "")}](${res.url})\n\n`;
        const ta = contentRef.current;
        if (ta) {
          const start = ta.selectionStart;
          const end = ta.selectionEnd;
          const next = editing.content.slice(0, start) + md + editing.content.slice(end);
          setEditing({ ...editing, content: next });
        } else {
          setEditing({ ...editing, content: editing.content + md });
        }
      }
    } catch (e) {
      alert(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  const save = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    try {
      await upsertFn({
        data: {
          id: editing.id ?? undefined,
          title: editing.title,
          slug: editing.slug || undefined,
          excerpt: editing.excerpt || undefined,
          content: editing.content,
          cover_image_url: editing.cover_image_url || undefined,
          category: editing.category || undefined,
          author: editing.author || undefined,
          published: editing.published,
        },
      });
      setEditing(null);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a: Article) => {
    if (!confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await deleteFn({ data: { id: a.id } });
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Delete failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-foreground">Blog & Articles</h1>
          <p className="text-sm text-muted-foreground">
            Publish articles to your website. Each article gets its own page and URL.
          </p>
        </div>
        <button
          onClick={() => setEditing({ ...empty })}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          <Plus className="h-4 w-4" /> New article
        </button>
      </div>

      {err && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {err}
        </div>
      )}

      <div className="rounded-2xl border border-border bg-background overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading articles…
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-16 text-center text-sm text-muted-foreground">
            No articles yet. Click "New article" to publish your first one.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {items.map((a) => (
              <div key={a.id} className="flex items-center gap-4 p-4">
                <div className="h-16 w-24 rounded-lg bg-muted overflow-hidden shrink-0">
                  {a.cover_image_url ? (
                    <img src={a.cover_image_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                      <ImageIcon className="h-5 w-5" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-medium text-foreground truncate">{a.title}</h3>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                        a.published
                          ? "bg-green-100 text-green-800"
                          : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {a.published ? "Published" : "Draft"}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground truncate mt-1">
                    /blog/{a.slug}
                    {a.category && <> · {a.category}</>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {a.published && (
                    <a
                      href={`/blog/${a.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-md border border-border p-2 hover:bg-muted"
                      title="View"
                    >
                      <Eye className="h-4 w-4" />
                    </a>
                  )}
                  <button
                    onClick={() =>
                      setEditing({
                        id: a.id,
                        title: a.title,
                        slug: a.slug,
                        excerpt: a.excerpt ?? "",
                        content: a.content,
                        cover_image_url: a.cover_image_url ?? "",
                        category: a.category ?? "",
                        author: a.author ?? "",
                        published: a.published,
                      })
                    }
                    className="rounded-md border border-border p-2 hover:bg-muted"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => remove(a)}
                    className="rounded-md border border-destructive/30 p-2 text-destructive hover:bg-destructive/10"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center overflow-y-auto p-0 sm:p-6">
          <form
            onSubmit={save}
            className="bg-background w-full sm:max-w-3xl sm:rounded-2xl border border-border shadow-2xl my-0 sm:my-6"
          >
            <div className="sticky top-0 bg-background border-b border-border px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                <h2 className="font-display text-xl">
                  {editing.id ? "Edit article" : "New article"}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-md p-2 hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <Field label="Title" required>
                <input
                  required
                  maxLength={200}
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  className="admin-input"
                  placeholder="A great Moroccan adventure"
                />
              </Field>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Slug (optional — auto from title)">
                  <input
                    maxLength={80}
                    value={editing.slug}
                    onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                    className="admin-input"
                    placeholder="moroccan-adventure"
                  />
                </Field>
                <Field label="Category">
                  <input
                    maxLength={80}
                    value={editing.category}
                    onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                    className="admin-input"
                    placeholder="Travel Guides"
                  />
                </Field>
              </div>

              <Field label="Author">
                <input
                  maxLength={120}
                  value={editing.author}
                  onChange={(e) => setEditing({ ...editing, author: e.target.value })}
                  className="admin-input"
                />
              </Field>

              <Field label="Excerpt (short summary)">
                <textarea
                  maxLength={500}
                  rows={2}
                  value={editing.excerpt}
                  onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                  className="admin-input"
                  placeholder="Shown on the blog list and in social previews"
                />
              </Field>

              <Field label="Cover image">
                <div className="flex items-center gap-4">
                  <div className="h-20 w-32 rounded-lg bg-muted overflow-hidden shrink-0">
                    {editing.cover_image_url ? (
                      <img src={editing.cover_image_url} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                        <ImageIcon className="h-5 w-5" />
                      </div>
                    )}
                  </div>
                  <label className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted">
                    {uploading === "cover" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ImageIcon className="h-4 w-4" />
                    )}
                    {editing.cover_image_url ? "Change image" : "Upload image"}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadImage(f, "cover");
                        e.target.value = "";
                      }}
                    />
                  </label>
                  {editing.cover_image_url && (
                    <button
                      type="button"
                      onClick={() => setEditing({ ...editing, cover_image_url: "" })}
                      className="text-xs text-muted-foreground hover:text-destructive"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </Field>

              <Field label="Content">
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-xs text-muted-foreground">
                      Use blank lines for paragraphs. Start a line with{" "}
                      <code className="px-1 py-0.5 bg-muted rounded">##</code> for a heading.
                      Add inline images with the button below.
                    </p>
                    <label className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs cursor-pointer hover:bg-muted">
                      {uploading === "inline" ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <ImageIcon className="h-3.5 w-3.5" />
                      )}
                      Insert image
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadImage(f, "inline");
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </div>
                  <textarea
                    ref={contentRef}
                    required
                    rows={14}
                    maxLength={50000}
                    value={editing.content}
                    onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                    className="admin-input font-mono text-sm leading-relaxed"
                    placeholder={`Write your article here.\n\n## A section heading\n\nA paragraph of body text.\n\n![Caption](image-url)`}
                  />
                </div>
              </Field>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editing.published}
                  onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                  className="h-4 w-4"
                />
                Publish immediately (visible on /blog)
              </label>
            </div>

            <div className="sticky bottom-0 bg-background border-t border-border px-6 py-4 flex items-center justify-end gap-2 rounded-b-2xl">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {editing.id ? "Save changes" : "Publish article"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-foreground">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
